import { createSlice, createAsyncThunk, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { request } from "../../services/cpln";
import { RootState } from "..";

// Enums
export enum BROWSER_REL {
  service = "service",
  region = "region",
  type = "type",
  item = "item",
  child = "child",
  self = "self",
  aspect = "aspect",
  roles = "roles",
  next = "next",
}

// Types
export interface BrowserLink {
  _type: "link";
  rel: BROWSER_REL;
  href: string;
  label?: string;
  dataItems: BrowserData[];
}

export interface BrowserData {
  _type: "data";
  _cpln: {
    label: string;
    ref: string;
    href?: string;
    mountUri?: string;
    hasChildren?: boolean;
  };
}

export interface BrowserRes {
  data?: BrowserData[];
  links: BrowserLink[];
}

export interface BrowsedItem {
  _cpln: { ref: string; label: string };
}

interface LinkSection {
  label: string;
  selectedHref?: string;
  items: BrowserLink[];
}

interface DataSection {
  label: string;
  items: BrowserData[];
  itemSelection?: string;
  childItems: BrowserLink[];
  childSelection?: string;
  items_2: BrowserData[];
  itemSelection_2?: string;
  childItems_2: BrowserLink[];
  childSelection_2?: string;
  items_3: BrowserData[];
  itemSelection_3?: string;
  childItems_3: BrowserLink[];
  childSelection_3?: string;
  items_4: BrowserData[];
  itemSelection_4?: string;
}

export interface BrowserServiceState {
  cloudaccountLink: string;
  canReach: boolean;
  reachError: string;
  servicesSection: LinkSection;
  regionsSection: LinkSection;
  typesSection: LinkSection;
  dataSection: DataSection;
  isConfirmLoading: boolean;
}

// Initial state
const initialState: BrowserServiceState = {
  cloudaccountLink: "",
  canReach: false,
  reachError: "",
  servicesSection: {
    label: "Services",
    selectedHref: undefined,
    items: [],
  },
  regionsSection: {
    label: "Regions",
    selectedHref: undefined,
    items: [],
  },
  typesSection: {
    label: "Types",
    selectedHref: undefined,
    items: [],
  },
  dataSection: {
    label: "",
    items: [],
    itemSelection: undefined,
    childItems: [],
    childSelection: undefined,
    items_2: [],
    itemSelection_2: undefined,
    childItems_2: [],
    childSelection_2: undefined,
    items_3: [],
    itemSelection_3: undefined,
    childItems_3: [],
    childSelection_3: undefined,
    items_4: [],
    itemSelection_4: undefined,
  },
  isConfirmLoading: false,
};

// Async thunks
export const checkCanReach = createAsyncThunk(
  "browserService/checkCanReach",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { cloudaccountLink } = state.browserService;

    try {
      await request({ service: "browser", url: `/browse${cloudaccountLink}` });
      return { canReach: true, reachError: "" };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      return { canReach: false, reachError: errorMessage };
    }
  },
);

export const fetchServices = createAsyncThunk(
  "browserService/fetchServices",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { cloudaccountLink } = state.browserService;

    try {
      let res: BrowserRes;
      const { data } = await request<BrowserRes>({
        service: "browser",
        url: `/browse${cloudaccountLink}`,
      });
      res = data;

      let services = res.links.filter((l) => l.rel === BROWSER_REL.service);
      let nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;

      while (nextLink) {
        const { data: nextData } = await request<BrowserRes>({
          service: "browser",
          url: nextLink,
        });
        res = nextData;
        services = services.concat(res.links.filter((l) => l.rel === BROWSER_REL.service));
        nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;
      }

      return services.map((s) => ({
        _type: "link" as const,
        rel: s.rel,
        label: s.label || "",
        href: s.href,
        dataItems: [],
      }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchRegions = createAsyncThunk(
  "browserService/fetchRegions",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const selectedService = state.browserService.servicesSection.items.find(
      (i) => i.href === state.browserService.servicesSection.selectedHref,
    );

    if (!selectedService) {
      return rejectWithValue("No service selected");
    }

    try {
      let res: BrowserRes;
      const { data } = await request<BrowserRes>({
        service: "browser",
        url: selectedService.href,
      });
      res = data;

      let regions = res.links.filter((l) => l.rel === BROWSER_REL.region);
      let nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;

      while (nextLink) {
        const { data: nextData } = await request<BrowserRes>({
          service: "browser",
          url: nextLink,
        });
        res = nextData;
        regions = regions.concat(res.links.filter((l) => l.rel === BROWSER_REL.region));
        nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;
      }

      return regions.map((r) => ({
        _type: "link" as const,
        rel: r.rel,
        label: r.label || "",
        href: r.href,
        dataItems: [],
      }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchTypes = createAsyncThunk("browserService/fetchTypes", async (_, { getState, rejectWithValue }) => {
  const state = getState() as RootState;
  const selectedRegion = state.browserService.regionsSection.items.find(
    (i) => i.href === state.browserService.regionsSection.selectedHref,
  );

  if (!selectedRegion) {
    return rejectWithValue("No region selected");
  }

  try {
    let res: BrowserRes;
    const { data } = await request<BrowserRes>({
      service: "browser",
      url: selectedRegion.href,
    });
    res = data;

    let types = res.links.filter((l) => l.rel === BROWSER_REL.type);
    let nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;

    while (nextLink) {
      const { data: nextData } = await request<BrowserRes>({
        service: "browser",
        url: nextLink,
      });
      res = nextData;
      types = types.concat(res.links.filter((l) => l.rel === BROWSER_REL.type));
      nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;
    }

    return types.map((t) => ({
      _type: "link" as const,
      rel: t.rel,
      label: t.label || "",
      href: t.href,
      dataItems: [],
    }));
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || error.message;
    return rejectWithValue(errorMessage);
  }
});

export const fetchDataItems = createAsyncThunk(
  "browserService/fetchDataItems",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const selectedType = state.browserService.typesSection.items.find(
      (i) => i.href === state.browserService.typesSection.selectedHref,
    );

    if (!selectedType) {
      return rejectWithValue("No type selected");
    }

    try {
      let res: BrowserRes;
      const { data } = await request<BrowserRes>({
        service: "browser",
        url: selectedType.href,
      });
      res = data;

      let dataItems = res.data || [];
      let nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;

      while (nextLink) {
        const { data: nextData } = await request<BrowserRes>({
          service: "browser",
          url: nextLink,
        });
        res = nextData;
        if (res.data) dataItems = dataItems.concat(res.data || []);
        nextLink = res.links.find((l) => l.rel === BROWSER_REL.next)?.href;
      }

      return {
        label: selectedType.label || "",
        items: dataItems.map((i) => ({
          _type: "data" as const,
          _cpln: {
            hasChildren: i._cpln.hasChildren,
            href: i._cpln.href,
            label: i._cpln.label,
            ref: i._cpln.ref,
            mountUri: i._cpln.mountUri,
          },
        })),
      };
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      return rejectWithValue(errorMessage);
    }
  },
);

export const onSelectDataItem = createAsyncThunk(
  "browserService/onSelectDataItem",
  async (href: string, { getState }) => {
    const state = getState() as RootState;
    const { dataSection } = state.browserService;

    // Find which level the item is at
    let item: BrowserData | undefined;
    let level = 0;

    item = dataSection.items_4.find((i) => i._cpln.href === href);
    if (item) level = 4;

    if (!item) {
      item = dataSection.items_3.find((i) => i._cpln.href === href);
      if (item) level = 3;
    }

    if (!item) {
      item = dataSection.items_2.find((i) => i._cpln.href === href);
      if (item) level = 2;
    }

    if (!item) {
      item = dataSection.items.find((i) => i._cpln.href === href);
      if (item) level = 1;
    }

    if (!item || !item._cpln.hasChildren || !item._cpln.href) {
      return { level, href, childItems: [] };
    }

    // Fetch child items
    const { data } = await request<BrowserRes>({
      service: "browser",
      url: item._cpln.href,
    });

    const childItems = data.links
      .filter((l) => l.rel === BROWSER_REL.child)
      .map((l) => ({
        _type: "link" as const,
        rel: l.rel,
        label: l.label || "",
        href: l.href,
        dataItems: [],
      }));

    return { level, href, childItems };
  },
);

export const onSelectLinkItem = createAsyncThunk(
  "browserService/onSelectLinkItem",
  async (href: string, { getState }) => {
    const state = getState() as RootState;
    const { dataSection } = state.browserService;

    // Find which level the link is at
    let childItem: BrowserLink | undefined;
    let level = 0;

    childItem = dataSection.childItems_3.find((i) => i.href === href);
    if (childItem) level = 3;

    if (!childItem) {
      childItem = dataSection.childItems_2.find((i) => i.href === href);
      if (childItem) level = 2;
    }

    if (!childItem) {
      childItem = dataSection.childItems.find((i) => i.href === href);
      if (childItem) level = 1;
    }

    if (!childItem) {
      return { level: 0, href, dataItems: [] };
    }

    // Fetch data items
    const { data } = await request<BrowserRes>({
      service: "browser",
      url: childItem.href,
    });

    if (!data.data) {
      return { level, href, dataItems: [] };
    }

    const dataItems = data.data.map((d) => ({
      _type: "data" as const,
      _cpln: {
        hasChildren: d._cpln.hasChildren,
        href: d._cpln.href,
        label: d._cpln.label,
        ref: d._cpln.ref,
        mountUri: d._cpln.mountUri,
      },
    }));

    return { level, href, dataItems };
  },
);

// Slice
const browserServiceSlice = createSlice({
  name: "browserService",
  initialState,
  reducers: {
    setCloudaccountLink: (state, action: PayloadAction<string>) => {
      state.cloudaccountLink = action.payload;
    },
    setIsConfirmLoading: (state, action: PayloadAction<boolean>) => {
      state.isConfirmLoading = action.payload;
    },
    setCanReach: (state, action: PayloadAction<boolean>) => {
      state.canReach = action.payload;
    },
    clearReachError: (state) => {
      state.reachError = "";
    },
    selectService: (state, action: PayloadAction<string>) => {
      state.servicesSection.selectedHref = action.payload;
    },
    selectRegion: (state, action: PayloadAction<string>) => {
      state.regionsSection.selectedHref = action.payload;
    },
    selectType: (state, action: PayloadAction<string>) => {
      state.typesSection.selectedHref = action.payload;
      const item = state.typesSection.items.find((i) => i.href === action.payload);
      state.dataSection.label = item?.label || "";
    },
    clearServicesSection: (state) => {
      state.servicesSection.items = [];
      state.servicesSection.selectedHref = undefined;
    },
    clearRegionsSection: (state) => {
      state.regionsSection.items = [];
      state.regionsSection.selectedHref = undefined;
    },
    clearTypesSection: (state) => {
      state.typesSection.items = [];
      state.typesSection.selectedHref = undefined;
    },
    clearDataSection: (state) => {
      state.dataSection.items = [];
      state.dataSection.itemSelection = undefined;
      state.dataSection.childItems = [];
      state.dataSection.childSelection = undefined;
      state.dataSection.items_2 = [];
      state.dataSection.itemSelection_2 = undefined;
      state.dataSection.childItems_2 = [];
      state.dataSection.childSelection_2 = undefined;
      state.dataSection.items_3 = [];
      state.dataSection.itemSelection_3 = undefined;
      state.dataSection.childItems_3 = [];
      state.dataSection.childSelection_3 = undefined;
      state.dataSection.items_4 = [];
      state.dataSection.itemSelection_4 = undefined;
    },
  },
  extraReducers: (builder) => {
    // checkCanReach
    builder.addCase(checkCanReach.fulfilled, (state, action) => {
      state.canReach = action.payload.canReach;
      state.reachError = action.payload.reachError;
    });

    // fetchServices
    builder.addCase(fetchServices.pending, (state) => {
      state.servicesSection.items = [];
      state.servicesSection.selectedHref = undefined;
      state.regionsSection.items = [];
      state.regionsSection.selectedHref = undefined;
      state.typesSection.items = [];
      state.typesSection.selectedHref = undefined;
      state.dataSection.items = [];
      state.dataSection.itemSelection = undefined;
    });
    builder.addCase(fetchServices.fulfilled, (state, action) => {
      state.servicesSection.items = action.payload;
    });

    // fetchRegions
    builder.addCase(fetchRegions.pending, (state) => {
      state.regionsSection.items = [];
      state.regionsSection.selectedHref = undefined;
      state.typesSection.items = [];
      state.typesSection.selectedHref = undefined;
      state.dataSection.items = [];
      state.dataSection.itemSelection = undefined;
    });
    builder.addCase(fetchRegions.fulfilled, (state, action) => {
      state.regionsSection.items = action.payload;
    });

    // fetchTypes
    builder.addCase(fetchTypes.pending, (state) => {
      state.typesSection.items = [];
      state.typesSection.selectedHref = undefined;
      state.dataSection.items = [];
      state.dataSection.itemSelection = undefined;
    });
    builder.addCase(fetchTypes.fulfilled, (state, action) => {
      state.typesSection.items = action.payload;
    });

    // fetchDataItems
    builder.addCase(fetchDataItems.pending, (state) => {
      state.dataSection.items = [];
      state.dataSection.itemSelection = undefined;
    });
    builder.addCase(fetchDataItems.fulfilled, (state, action) => {
      state.dataSection.label = action.payload.label;
      state.dataSection.items = action.payload.items;
    });

    // onSelectDataItem
    builder.addCase(onSelectDataItem.fulfilled, (state, action) => {
      const { level, href, childItems } = action.payload;

      // Clear items at higher levels
      if (level <= 3) {
        state.dataSection.items_4 = [];
        state.dataSection.itemSelection_4 = undefined;
      }
      if (level <= 2) {
        state.dataSection.childItems_3 = [];
        state.dataSection.childSelection_3 = undefined;
        state.dataSection.items_3 = [];
        state.dataSection.itemSelection_3 = undefined;
      }
      if (level <= 1) {
        state.dataSection.childItems_2 = [];
        state.dataSection.childSelection_2 = undefined;
        state.dataSection.items_2 = [];
        state.dataSection.itemSelection_2 = undefined;
      }
      if (level === 0) {
        state.dataSection.childItems = [];
        state.dataSection.childSelection = undefined;
      }

      // Set selection and child items
      switch (level) {
        case 1:
          state.dataSection.itemSelection = href;
          state.dataSection.childItems = childItems;
          break;
        case 2:
          state.dataSection.itemSelection_2 = href;
          state.dataSection.childItems_2 = childItems;
          break;
        case 3:
          state.dataSection.itemSelection_3 = href;
          state.dataSection.childItems_3 = childItems;
          break;
        case 4:
          state.dataSection.itemSelection_4 = href;
          break;
      }
    });

    // onSelectLinkItem
    builder.addCase(onSelectLinkItem.fulfilled, (state, action) => {
      const { level, href, dataItems } = action.payload;

      // Clear items at level 4
      state.dataSection.items_4 = [];
      state.dataSection.itemSelection_4 = undefined;

      // Clear items at higher levels if needed
      if (level <= 2) {
        state.dataSection.items_3 = [];
        state.dataSection.itemSelection_3 = undefined;
        state.dataSection.childSelection_3 = undefined;
      }
      if (level <= 1) {
        state.dataSection.items_2 = [];
        state.dataSection.itemSelection_2 = undefined;
        state.dataSection.childSelection_2 = undefined;
      }

      // Set selection and data items
      switch (level) {
        case 1:
          state.dataSection.childSelection = href;
          state.dataSection.items_2 = dataItems;
          break;
        case 2:
          state.dataSection.childSelection_2 = href;
          state.dataSection.items_3 = dataItems;
          break;
        case 3:
          state.dataSection.childSelection_3 = href;
          state.dataSection.items_4 = dataItems;
          break;
      }
    });
  },
});

// Actions
export const {
  setCloudaccountLink,
  setIsConfirmLoading,
  setCanReach,
  clearReachError,
  selectService,
  selectRegion,
  selectType,
  clearServicesSection,
  clearRegionsSection,
  clearTypesSection,
  clearDataSection,
} = browserServiceSlice.actions;

// Selectors
export const selectBrowserService = (state: RootState) => state.browserService;

export const selectCloudaccountName = createSelector([selectBrowserService], (browserService) => {
  if (browserService.cloudaccountLink.length < 1) {
    return "";
  }
  return browserService.cloudaccountLink.split("/")[4];
});

export const selectSelectedService = createSelector([selectBrowserService], (browserService) =>
  browserService.servicesSection.items.find((i) => i.href === browserService.servicesSection.selectedHref),
);

export const selectSelectedRegion = createSelector([selectBrowserService], (browserService) =>
  browserService.regionsSection.items.find((i) => i.href === browserService.regionsSection.selectedHref),
);

export const selectSelectedType = createSelector([selectBrowserService], (browserService) =>
  browserService.typesSection.items.find((i) => i.href === browserService.typesSection.selectedHref),
);

// Helper selectors for checking if items are children
export const isChildLink = (link: BrowserLink) => link.rel === BROWSER_REL.child;
export const isTargetData = (data: BrowserData) => !data._cpln.hasChildren;

export const browserServiceReducer = browserServiceSlice.reducer;
